import styled from '@emotion/styled';
import { Typography } from 'src/components-dummy';
import {
  SwitchContainerStyled,
  SwitchLabelStyled,
  TitleTemplateStyled,
  SubTitleTemplateStyled,
} from 'src/components-dummy/Switch/Switch.styles';

export const SettingsLayout = styled.div`
  display: flex;
  gap: 24px;
  align-items: flex-start;
  padding-bottom: 80px;

  @media (max-width: 1100px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const SettingsPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
`;

export const PreviewPanel = styled.div`
  width: 35%;
  flex-shrink: 0;

  @media (max-width: 1100px) {
    width: 100%;
  }
`;

export const SectionCard = styled.div`
  width: 100%;
  border: 1px solid ${({ theme }) => theme.palette.custom['gray-60']};
  border-radius: 10px;
  padding: 32px;
  background: ${({ theme }) => theme.palette.custom.white};
`;

export const SectionHeader = styled.div`
  padding-bottom: 16px;
  border-bottom: 1px solid ${({ theme }) => theme.palette.custom['gray-70']};
  margin-bottom: 20px;
`;

export const SectionTitle = styled(Typography)`
  color: ${({ theme }) => theme.palette.custom['black-10']};
  display: block;
  margin-bottom: 4px;
`;

export const SectionDescription = styled(Typography)`
  color: #6b6b6b;
  display: block;
`;

export const SourceButtonsRow = styled.div`
  display: flex;
  gap: 8px;
`;

export const SourceButton = styled.button<{ selected?: boolean }>`
  flex: 1;
  padding: 12px 14px;
  border-radius: 8px;
  border: 1.5px solid
    ${({ selected, theme }) =>
      selected ? theme.palette.custom['primary-main'] : theme.palette.custom['gray-60']};
  background-color: ${({ selected, theme }) =>
    selected ? theme.palette.custom['primary-main'] : theme.palette.custom.white};
  cursor: pointer;
  text-align: left;
  transition:
    border-color 0.15s ease,
    background-color 0.15s ease;

  &:hover {
    border-color: ${({ selected, theme }) =>
      selected ? theme.palette.custom['primary-main'] : theme.palette.custom['gray-30']};
  }
`;

export const SourceButtonInner = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const SourceButtonIcon = styled.div<{ selected?: boolean }>`
  flex-shrink: 0;

  svg {
    width: 18px;
    height: 18px;
    display: block;
    /* catalog-manager uses stroke="currentColor"; upload uses hardcoded stroke */
    color: ${({ selected, theme }) =>
      selected ? theme.palette.custom.white : theme.palette.custom['gray-10']};
  }

  svg path {
    stroke: ${({ selected, theme }) =>
      selected ? theme.palette.custom.white : theme.palette.custom['gray-10']};
  }
`;

export const SourceButtonTitle = styled.span<{ selected?: boolean }>`
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: ${({ selected, theme }) =>
    selected ? theme.palette.custom.white : theme.palette.custom['black-10']};
  line-height: 18px;
`;

export const SourceButtonSubtitle = styled.span<{ selected?: boolean }>`
  display: block;
  font-size: 10px;
  font-weight: 400;
  color: ${({ selected, theme }) =>
    selected ? 'rgba(255,255,255,0.75)' : theme.palette.custom['gray-30']};
  line-height: 16px;
  margin-top: 2px;
`;

export const PersonalizeBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  background: ${({ theme }) => theme.palette.custom['gray-80']};
  border-radius: 8px;
  margin-top: 8px;
  gap: 12px;

  ${SwitchContainerStyled} {
    flex-direction: row-reverse;
    justify-content: space-between;
    width: 100%;
  }

  ${SwitchLabelStyled} {
    margin-left: 0;
  }

  ${TitleTemplateStyled} span {
    color: ${({ theme }) => theme.palette.custom['black-10']};
    font-size: 13px;
  }

  ${SubTitleTemplateStyled} span {
    color: #6b6b6b;
    font-size: 11px;
  }
`;

export const PersonalizeBoxIcon = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;

  svg {
    width: 18px;
    height: 18px;
    color: ${({ theme }) => theme.palette.custom['primary-main']};
  }

  svg path {
    stroke: ${({ theme }) => theme.palette.custom['primary-main']};
  }
`;

export const ToggleRow = styled.div`
  padding: 10px 0;
  border-bottom: 1px solid ${({ theme }) => theme.palette.custom['gray-80']};

  &:last-of-type {
    border-bottom: none;
  }

  ${SwitchContainerStyled} {
    flex-direction: row-reverse;
    justify-content: space-between;
    width: 100%;
  }

  ${SwitchLabelStyled} {
    margin-left: 0;
  }

  ${TitleTemplateStyled} span {
    color: ${({ theme }) => theme.palette.custom['black-10']};
    font-size: 13px;
  }

  ${SubTitleTemplateStyled} span {
    color: #6b6b6b;
    font-size: 11px;
  }
`;

export const AdvancedHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  cursor: pointer;
  user-select: none;
`;

export const AdvancedChevron = styled.span<{ open: boolean }>`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.palette.custom['gray-10']};
  transform: rotate(${({ open }) => (open ? '90deg' : '-90deg')});
  transition: transform 0.2s ease;
`;

export const PreviewHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  padding-bottom: 16px;
  border-bottom: 1px solid ${({ theme }) => theme.palette.custom['gray-70']};
  margin-bottom: 16px;
`;

export const PreviewTitle = styled(Typography)`
  color: ${({ theme }) => theme.palette.custom['black-10']};
`;

export const PreviewCount = styled(Typography)`
  color: #6b6b6b;
`;

export const PreviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
`;

export const PreviewItem = styled.div`
  background-color: ${({ theme }) => theme.palette.custom['gray-70']};
  border-radius: 6px;
  aspect-ratio: 1;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding-bottom: 8px;
  position: relative;
  overflow: hidden;
`;

export const PreviewItemEmpty = styled.div`
  background-color: ${({ theme }) => theme.palette.custom['gray-70']};
  border-radius: 6px;
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const PreviewItemEmptyLabel = styled.span`
  font-size: 10px;
  color: ${({ theme }) => theme.palette.custom['gray-30']};
`;

export const EmptyPreviewState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 24px;
  text-align: center;
  gap: 6px;
`;

export const EmptyPreviewIcon = styled.div`
  margin-bottom: 4px;
  opacity: 0.4;

  svg {
    width: 36px;
    height: 36px;
    /* user.svg uses stroke="#404040" — visible at reduced opacity */
  }
`;

export const EmptyPreviewText = styled(Typography)`
  color: #9a9a9a;
  display: block;
`;

export const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 16px 0;
  border-top: 1px solid ${({ theme }) => theme.palette.custom['gray-70']};
  margin-top: 4px;
`;

export const CurateTableHeader = styled.div`
  display: grid;
  grid-template-columns: 20px 28px 1fr 130px 1fr 28px;
  gap: 8px;
  padding: 0 0 6px;
  border-bottom: 1px solid ${({ theme }) => theme.palette.custom['gray-70']};
  margin-bottom: 4px;
`;

export const CurateColumnLabel = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: ${({ theme }) => theme.palette.custom['gray-30']};
`;

export const CurateRowsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-bottom: 12px;
  margin-bottom: 12px;
  border-bottom: 1px solid ${({ theme }) => theme.palette.custom['gray-70']};
`;

export const CurateRow = styled.div`
  display: grid;
  grid-template-columns: 20px 28px 1fr 130px 1fr 28px;
  gap: 8px;
  align-items: flex-start;
`;

export const DragHandleIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: grab;
  margin-top: 5px;
  opacity: 0.5;

  svg {
    width: 14px;
    height: 14px;
    /* drag-handle-vertical-double uses fill="#6E6E71" — visible by default */
  }

  &:hover {
    opacity: 0.8;
  }
`;

export const DeleteRowButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 5px;
  opacity: 0.45;

  svg {
    width: 16px;
    height: 16px;
    /* trash-can uses stroke="#6E6E71" */
  }

  &:hover {
    opacity: 1;

    svg path {
      stroke: ${({ theme }) => theme.palette.custom.red};
    }
  }
`;

export const CurateFieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

export const CurateInput = styled.input<{ error?: boolean }>`
  height: 33px;
  padding: 6px 9px;
  font-size: 12px;
  color: ${({ theme }) => theme.palette.custom['black-10']};
  background: ${({ theme }) => theme.palette.custom.white};
  border: 1px solid
    ${({ error, theme }) => (error ? theme.palette.custom.red : theme.palette.custom['gray-60'])};
  border-radius: 6px;
  outline: none;
  width: 100%;
  box-sizing: border-box;

  &::placeholder {
    color: ${({ theme }) => theme.palette.custom['gray-50']};
  }

  &:focus {
    border-color: ${({ error, theme }) =>
      error ? theme.palette.custom.red : theme.palette.custom['primary-main']};
  }
`;

export const CurateFieldError = styled.span`
  display: block;
  font-size: 10px;
  color: ${({ theme }) => theme.palette.custom.red};
  margin-top: 3px;
  margin-bottom: 8px;
`;

export const CurateFieldWarning = styled.span`
  font-size: 10px;
  color: #fac775;
  margin-top: 3px;
`;

export const AddUrlLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.palette.custom['primary-main']};
  cursor: pointer;
  text-align: left;

  &:disabled {
    color: ${({ theme }) => theme.palette.custom['gray-50']};
    cursor: default;
  }

  &:hover:not(:disabled) {
    text-decoration: underline;
  }
`;

export const AddLanguageButton = styled(AddUrlLink)`
  padding: 5px 12px;
  font-weight: 400;
  width: fit-content;

  &:hover:not(:disabled) {
    text-decoration: none;
  }
`;

export const BulkAddText = styled.span`
  display: block;
  font-size: 11px;
  color: ${({ theme }) => theme.palette.custom['gray-30']};
  margin-top: 4px;
`;

export const PreviewProductImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 6px;
`;

export const ImageSwatch = styled.img`
  width: 28px;
  height: 28px;
  border-radius: 5px;
  object-fit: cover;
  flex-shrink: 0;
  margin-top: 1px;
`;

export const LocaleUrlsToggle = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  user-select: none;
  margin: 8px 0 10px;

  svg {
    width: 12px;
    height: 12px;
    flex-shrink: 0;
  }
`;

export const LocaleRowsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-bottom: 14px;
`;

export const LocaleUrlsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 20px;
`;

export const LocaleUrlRow = styled.div`
  display: grid;
  grid-template-columns: 160px 1fr 28px;
  gap: 8px;
  align-items: center;
`;

export const LocaleSelectInput = styled.select`
  height: 34px;
  padding: 0 8px;
  font-size: 12px;
  color: ${({ theme }) => theme.palette.custom['black-10']};
  background: ${({ theme }) => theme.palette.custom.white};
  border: 1px solid ${({ theme }) => theme.palette.custom['gray-60']};
  border-radius: 6px;
  outline: none;
  width: 100%;
  box-sizing: border-box;
  cursor: pointer;

  &:focus {
    border-color: ${({ theme }) => theme.palette.custom['primary-main']};
  }
`;

export const LocaleUrlHint = styled.span`
  display: block;
  font-size: 12px;
  color: ${({ theme }) => theme.palette.custom['gray-30']};
  margin-top: 2px;
`;

export const LocaleUrlInput = styled(CurateInput)`
  height: 34px;
  font-size: 12px;
`;

export const BrokenImagePlaceholder = styled.div<{ hasError?: boolean }>`
  width: 28px;
  height: 28px;
  border-radius: 5px;
  flex-shrink: 0;
  margin-top: 1px;
  background-color: ${({ hasError, theme }) =>
    hasError ? theme.palette.custom['red-90'] : theme.palette.custom['gray-80']};
  border: 1px solid
    ${({ hasError, theme }) =>
      hasError ? theme.palette.custom['red-80'] : theme.palette.custom['gray-60']};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: ${({ hasError, theme }) =>
    hasError ? theme.palette.custom.red : theme.palette.custom['gray-50']};

  &::after {
    content: '${({ hasError }) => (hasError ? '✕' : '')}';
  }
`;
